import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const STRINGS = {
  en: {
    "good_morning": "Good morning",
    "good_afternoon": "Good afternoon",
    "good_evening": "Good evening",
    "where_to": "Where to?",
    "complimentary_sub": "Complimentary rides for ENEC employees.",
    "search_destination": "Search destination…",
    "quick_destinations": "Quick destinations",
    "usual_routes": "Usual routes",
    "active_ride": "Active ride",
    "cars_nearby": "cars nearby",
    "home": "Home",
    "rides": "Rides",
    "bus": "Bus",
    "alerts": "Alerts",
    "profile": "Profile",
    "drive": "Drive",
    "trips": "Trips",
    "earnings": "Earnings",
    "sign_in": "Sign in",
    "sign_out": "Sign out",
    "welcome_back": "Welcome back",
    "email": "Email",
    "password": "Password",
    "or": "or",
    "uae_pass_btn": "Sign in with UAE Pass",
    "demo_accounts": "Demo accounts",
    "saved_places": "Saved places",
    "no_saved_places": "No saved places yet. Tap Add to star your home or favorite spots.",
    "set_home": "Set home",
    "favorite": "Favorite",
    "preferences": "Preferences",
    "language": "Language",
    "dark_mode": "Dark mode",
    "notifications": "Notifications",
    "support": "Support",
    "about_teslam": "About TESLAM",
    "help_emergencies": "Help & emergencies",
    "add": "Add",
    "add_place": "Add a place",
    "save": "Save",
    "cancel": "Cancel",
    "on": "On",
    "off": "Off",
    "language_en": "English",
    "language_ar": "العربية",
    "co2_month": "CO₂ saved · this month",
    "trees_equiv": "Trees ≡",
    "lifetime": "Lifetime",
    "my_co2": "My CO₂ impact",
    "tier": "tier",
    "personal_info": "Personal info",
    "secured_by": "Secured by ENEC · Abu Dhabi",
    "trip": "Trip",
    "eta": "ETA",
    "book_now": "Book Now",
    "complimentary": "Complimentary",
    "complimentary_banner": "COMPLIMENTARY · COVERED BY ENEC",
    "seats_needed": "Seats needed",
    "all_caught_up": "All caught up",
    "mark_all_read": "Mark all read",
    "i_am_employee": "I'm an Employee",
    "driver_login": "Driver Login",
    "admin_console": "Admin Console",
    "be_safe": "Be Safe.",
    "travel_smart": "Travel Smart.",
    "splash_sub": "The single transport platform for every ENEC employee, driver and dispatcher. From Abu Dhabi to Barakah, in a tap.",
  },
  ar: {
    "good_morning": "صباح الخير",
    "good_afternoon": "مساء الخير",
    "good_evening": "مساء الخير",
    "where_to": "إلى أين؟",
    "complimentary_sub": "رحلات مجانية لموظفي ENEC.",
    "search_destination": "ابحث عن الوجهة…",
    "quick_destinations": "وجهات سريعة",
    "usual_routes": "المسارات المعتادة",
    "active_ride": "رحلة نشطة",
    "cars_nearby": "سيارات قريبة",
    "home": "الرئيسية",
    "rides": "الرحلات",
    "bus": "حافلة",
    "alerts": "التنبيهات",
    "profile": "الملف",
    "drive": "القيادة",
    "trips": "الرحلات",
    "earnings": "الأرباح",
    "sign_in": "تسجيل الدخول",
    "sign_out": "تسجيل الخروج",
    "welcome_back": "مرحباً بعودتك",
    "email": "البريد الإلكتروني",
    "password": "كلمة المرور",
    "or": "أو",
    "uae_pass_btn": "الدخول عبر UAE Pass",
    "demo_accounts": "حسابات تجريبية",
    "saved_places": "الأماكن المحفوظة",
    "no_saved_places": "لا توجد أماكن محفوظة بعد. اضغط إضافة لحفظ منزلك أو أماكنك المفضلة.",
    "set_home": "اجعلها المنزل",
    "favorite": "مفضلة",
    "preferences": "التفضيلات",
    "language": "اللغة",
    "dark_mode": "الوضع الداكن",
    "notifications": "الإشعارات",
    "support": "الدعم",
    "about_teslam": "عن تسلم",
    "help_emergencies": "المساعدة والطوارئ",
    "add": "إضافة",
    "add_place": "إضافة مكان",
    "save": "حفظ",
    "cancel": "إلغاء",
    "on": "مفعل",
    "off": "متوقف",
    "language_en": "English",
    "language_ar": "العربية",
    "co2_month": "CO₂ موفّر · هذا الشهر",
    "trees_equiv": "أشجار ≡",
    "lifetime": "مدى الحياة",
    "my_co2": "أثري البيئي",
    "tier": "المستوى",
    "personal_info": "المعلومات الشخصية",
    "secured_by": "بتأمين من ENEC · أبوظبي",
    "trip": "الرحلة",
    "eta": "الوصول",
    "book_now": "احجز الآن",
    "complimentary": "مجاناً",
    "complimentary_banner": "مجانية · تغطيها ENEC",
    "seats_needed": "عدد المقاعد",
    "all_caught_up": "لا توجد إشعارات",
    "mark_all_read": "تعليم الكل مقروء",
    "i_am_employee": "أنا موظف",
    "driver_login": "دخول السائق",
    "admin_console": "لوحة الإدارة",
    "be_safe": "كن آمناً.",
    "travel_smart": "تنقّل بذكاء.",
    "splash_sub": "منصة النقل الموحّدة لكل موظف وسائق ومسؤول في ENEC. من أبوظبي إلى براكة، بضغطة واحدة.",
  },
};

const I18nCtx = createContext({ lang: "en", t: (k) => k, setLang: () => {}, dir: "ltr" });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("teslam_lang") || "en");

  const setLang = useCallback(async (l) => {
    setLangState(l);
    localStorage.setItem("teslam_lang", l);
    document.documentElement.lang = l;
    document.documentElement.dir = l === "ar" ? "rtl" : "ltr";
    try {
      await api.patch("/auth/me", { preferred_lang: l });
    } catch (e) {
      // best-effort, no auth okay
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);

  const t = useCallback((key) => STRINGS[lang]?.[key] || STRINGS.en[key] || key, [lang]);

  return (
    <I18nCtx.Provider value={{ lang, setLang, t, dir: lang === "ar" ? "rtl" : "ltr" }}>
      {children}
    </I18nCtx.Provider>
  );
}

export const useI18n = () => useContext(I18nCtx);
